import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { LogistikDialogDeliveryRequest } from '@chancenlogistik/client/components/dialogs/logistik-dialog-delivery-request.js';
import '@chancenlogistik/client/components/logistik-edit-order-item';
import { LogistikRoutes } from '@chancenlogistik/client/routes';
import '@chancenwerk/components/chancen-component-card-title.js';
import {
  LogistikIndividualOrderItem,
  LogistikOrder,
  LogistikOrderClearanceAlternative,
  LogistikOrderClearanceState,
  LogistikOrderDeliveryState,
} from '@chancenwerk/db/LogistikOrder.js';
import {
  LogistikOrderDeliveryStateEnum,
  LogistikShippingRecipient,
  LogistikShippingStatus,
} from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-loader.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { filter, firstValueFrom, switchMap, timer } from 'rxjs';
import { g_getShippingStatusColor, g_getShippingStatusIcon } from '../../../../src/logistik/client/helper.js';
import '../components/logistik-component-base-header.js';
import '../components/logistik-component-base-page-layout.js';

export const orderRouter = makeRouter({
  overview: {
    path: '/:orderID',
    render: ({ orderID }) =>
      html`<logistik-page-order-status-details .orderID=${orderID}></logistik-page-order-status-details>`,
  },
  'accept-order': {
    path: '/:orderID/accept',
    render: ({ orderID }) => {
      return html`<logistik-page-order-status-details
        .orderID=${orderID}
        .view=${'accept'}
      ></logistik-page-order-status-details>`;
    },
  },
});

/**
 * @customElement logistik-page-order-status-details
 */
@customElement('logistik-page-order-status-details')
export class LogistikPageOrderStatusDetails extends ChemistryLitElement {
  override styles() {
    return [...super.styles(), { p: { margin: 0 } }];
  }

  @property() orderID = new RXController(this, '');
  @property() view = new RXController(this, '');
  @property() triggerAccept = new RXController(this, false);

  @state() private readonly _order = new RXController(
    this,
    this.orderID.observable.pipe(
      filter(Boolean),
      switchMap(id => {
        return LogistikOrder.subscribeByID<LogistikOrder>(id)(LogistikOrder.allFields);
      }),
    ),
  );

  @state() private _acceptOrderStatus: 'pending' | 'processing' | 'completed' | 'error' = 'pending';

  override updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);

    if (changedProperties.has('_order')) {
      if (!this._order.value) return;
      if (this._order.value.deliveryStatus?.status === LogistikOrderDeliveryStateEnum.REQUESTED) {
        LogistikDialogDeliveryRequest.showPopup({ props: { order: this._order.value } });
      }
    }
  }

  override render() {
    if (this.triggerAccept.value) {
      this._acceptOrderDelivery();
      this.triggerAccept.next(false);
    }

    switch (this.view.value) {
      case 'accept':
        return this._acceptOrder();
    }

    return this._orderDetail();
  }

  private _orderDetail() {
    if (this._order.isLoading) {
      return html`<cr-loader></cr-loader>`;
    }
    return html`
      <cr-flex horizontal space-between crossaxis-center space="md">
        <cr-card padding="md">
          <cr-flex horizontal crossaxis-center space="md">
            <cr-h3>Status: </cr-h3>
            <cr-icon
              ${css({
                fontWeight: 'bold',
                fontSize: '1.7rem',
                color: g_getShippingStatusColor(
                  this._order.value?.clearanceState === LogistikOrderClearanceState.DECLINED
                    ? LogistikOrderClearanceState.DECLINED
                    : this._order.value?.status!,
                ),
              })}
            >
              ${g_getShippingStatusIcon(
                this._order.value?.clearanceState === LogistikOrderClearanceState.DECLINED
                  ? LogistikOrderClearanceState.DECLINED
                  : this._order.value?.status!,
              )}
            </cr-icon>
            <cr-h3
              ${css({
                fontWeight: 'bold',
                color: g_getShippingStatusColor(
                  this._order.value?.clearanceState === LogistikOrderClearanceState.DECLINED
                    ? LogistikOrderClearanceState.DECLINED
                    : this._order.value?.status!,
                ),
              })}
            >
              ${this._order.value?.clearanceState === LogistikOrderClearanceState.DECLINED
                ? 'Freigabe verweigert'
                : this._order.value?.status}
            </cr-h3>
          </cr-flex>
        </cr-card>
        <cr-flex crossaxis-center space="md">
          <cr-button
            icon="support"
            href=${`mailto: chancenlogistik@chancenwerk.org?subject=Anfrage zur Bestellung ${
              this._order.value?.orderNumber ? this._order.value.orderNumber : this._order.value?._id
            }`}
          >
            Support kontaktieren
          </cr-button>
        </cr-flex>
      </cr-flex>
      ${this._order.value?.clearanceState === LogistikOrderClearanceState.DECLINED
        ? html`
            <cr-card padding="md">
              <cr-flex space="md">
                <chancen-component-card-title> Informationen zum Verweigern der Freigabe </chancen-component-card-title>
                ${this._order.value.clearanceNote
                  ? html` <cr-text> ${unsafeHTML(this._order.value.clearanceNote)} </cr-text> `
                  : nothing}
                ${repeat(
                  this._order.value.clearanceAlternatives as LogistikOrderClearanceAlternative[],
                  e => e.oldLink,
                  (e, i) => {
                    return html`<cr-flex
                      space="md"
                      ${css({
                        padding: `${this.spacing.md} 0 ${this.spacing.md} ${this.spacing.md}`,
                        borderLeft: `4px solid ${this.colors.accent}`,
                      })}
                    >
                      <cr-flex horizontal crossaxis-center space="xs">
                        <cr-text bold>Alternative für: </cr-text>
                        <cr-link href=${e.oldLink} target="_blank">
                          <cr-text>Produkt</cr-text>
                        </cr-link>
                      </cr-flex>
                      <cr-text>
                        Link:
                        <cr-link> <cr-text> ${e.link} </cr-text> </cr-link>
                      </cr-text>
                      ${this._order.value?.clearanceAlternativePrice !== null
                        ? html`
                            <cr-text>
                              Preis:
                              ${Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(e.price ?? 0)}
                            </cr-text>
                          `
                        : nothing}
                    </cr-flex>`;
                  },
                )}
                ${this._order.value.status === LogistikShippingStatus.COMPLETED
                  ? html`
                      <cr-flex horizontal end> <cr-text tone="warning"> Alternative abgelehnt </cr-text> </cr-flex>
                    `
                  : html`
                      <cr-flex crossaxis-end wrap space="md">
                        <cr-flex horizontal end space="md">
                          <cr-button
                            mode="outline"
                            @click=${async () => {
                              if (
                                !this._order.value ||
                                !(this._order.value.items[0] instanceof LogistikIndividualOrderItem) ||
                                !this._order.value.clear
                              )
                                return;

                              try {
                                if (
                                  !(await ChromiumDialog.confirm('Möchten Sie diese Bestellung wirklich stornieren?'))
                                )
                                  return;

                                this._order.value.status = LogistikShippingStatus.COMPLETED;
                                this.requestUpdate();

                                await ChromiumDialog.waitFor(this._order.value.save(), 'Bestellung wird storniert...');
                              } catch (err) {
                                logger.error(
                                  { err },
                                  'Es gab ein Problem beim Stornieren einer individuellen Bestellung',
                                );
                                return ChromiumDialog.alert(
                                  'Es gab ein Problem beim Stornieren. Bitte versuchen Sie es später erneut',
                                );
                              }
                            }}
                          >
                            Stornieren
                          </cr-button>
                          <cr-button
                            icon="send"
                            @click=${async () => {
                              if (
                                !this._order.value ||
                                !(this._order.value.items[0] instanceof LogistikIndividualOrderItem) ||
                                !this._order.value.clearanceAlternatives
                              )
                                return;

                              this._order.value.clearanceState = LogistikOrderClearanceState.APPROVED;

                              (this._order.value.clearanceAlternatives as LogistikOrderClearanceAlternative[]).forEach(
                                e => {
                                  const index = this._order.value.items.findIndex(i => i.link === e.oldLink);
                                  if (index === -1) return;
                                  this._order.value.items[index].link = e.link;
                                  if (e.price !== null) this._order.value.items[index].price = e.price!;
                                },
                              );

                              try {
                                const lieferanweisungen = await ChromiumDialog.showPopup<null | string>({
                                  content: resolve => {
                                    let note = this._order.value?.shippingDetails.shippingInstructions ?? '';
                                    return html`
                                      <cr-flex w100 h100 space="lg" padding="md">
                                        <x-flex horizontal crossaxis-center space="md">
                                          <cr-icon
                                            ${css({ fontSize: this.spacing.md, color: this.colors.tone.success })}
                                          >
                                            check_circle
                                          </cr-icon>
                                          <cr-h3> Alternative akzeptieren </cr-h3>
                                        </x-flex>
                                        <cr-text tone="subtle">
                                          Hier können Sie die Bestellalternative akzeptieren. Sollte es von dem
                                          vorgeschlagenen Produkt mehrere Ausführungen geben, können Sie uns mitteilen,
                                          welche wir bestellen sollen. Beispielsweise wenn ein Produkt in verschiedenen
                                          Farben erhältlich ist.
                                        </cr-text>
                                        <logistik-component-tinymce
                                          @value-picked=${(e: ValueEvent<string>) => (note = e.detail.value)}
                                        ></logistik-component-tinymce>
                                        <x-flex horizontal end space="sm">
                                          <cr-button mode="outline" @click=${() => resolve(null)}>
                                            Abbrechen
                                          </cr-button>
                                          <cr-button @click=${async () => resolve(note)}> Freigeben </cr-button>
                                        </x-flex>
                                      </cr-flex>
                                    `;
                                  },
                                });
                                if (lieferanweisungen === null) return;

                                this._order.value.shippingDetails.shippingInstructions = lieferanweisungen;
                                this.requestUpdate();

                                await ChromiumDialog.waitFor(
                                  this._order.value.save(),
                                  'Alternative wird akzeptiert...',
                                );
                              } catch (err) {
                                logger.error({ err }, 'Es gab ein Problem beim Akzeptieren einer Alternative');
                                return ChromiumDialog.alert(
                                  'Es gab ein Problem beim Akzeptieren der Alternative. Bitte versuchen Sie es später erneut',
                                );
                              }
                            }}
                          >
                            Akzeptieren
                          </cr-button>
                        </cr-flex>
                        <cr-text small tone="subtle">
                          Sollten Sie mit der Alternative nicht zufrieden sein, erstellen Sie eine neue individuelle
                          Bestellungen oder wenden Sie sich direkt an das Logistik-Team!
                        </cr-text>
                      </cr-flex>
                    `}
              </cr-flex>
            </cr-card>
          `
        : nothing}
      ${this._order.value?.isExpress
        ? html`
            <logistik-component-error-message type="success">
              Expresslieferung angefragt
            </logistik-component-error-message>
          `
        : nothing}
      <cr-flex horizontal wrap space="md">
        <cr-card flex ${css({ minWidth: '350px' })} padding="md" space="md">
          <chancen-component-card-title>Inhalt </chancen-component-card-title>
          ${this._order.value?.items.map(
            (item, i) =>
              html`
                <logistik-edit-order-item
                  .order=${this._order.value}
                  .orderItem=${item}
                  .index=${i}
                ></logistik-edit-order-item>
              `,
          )}
        </cr-card>
        <cr-card flex ${css({ minWidth: '350px' })} padding="md" space="md">
          <chancen-component-card-title>Details </chancen-component-card-title>
          <cr-grid columns="max-content 1fr" space="md">
            ${this._order.value?.orderNumber ? html`<cr-text bold>Bestellnummer:</cr-text>` : nothing}
            ${this._order.value?.orderNumber ? html`<cr-text>${this._order.value.orderNumber}</cr-text>` : nothing}
            ${this._order.value?.processor
              ? html`
                  <cr-text bold>Bearbeiter:</cr-text>
                  <cr-text> ${this._order.value.processor.vorname} ${this._order.value.processor.nachname} </cr-text>
                `
              : nothing}

            <cr-text bold>Bestelldatum:</cr-text>
            <cr-text>${this._order.value?.createdAt.toFormat('dd.MM.yyyy')}</cr-text>

            <cr-text bold>Auftraggeber:</cr-text>
            <cr-text>${this._order.value?.shippingDetails.customerEmail}</cr-text>

            <cr-text bold>Standort:</cr-text>
            <cr-text>${this._order.value?.shippingDetails.shippingSchool?.name ?? '-'}</cr-text>

            <cr-text bold>Lieferadresse:</cr-text>
            <cr-flex space="md">
              ${this._order.value?.shippingDetails.shippingRecipient === LogistikShippingRecipient.SCHOOL
                ? html`
                    <cr-text> ${this._order.value.shippingDetails.shippingSchool?.address?.strasse ?? '-'} </cr-text>
                    <cr-text> ${this._order.value.shippingDetails.shippingSchool?.address?.zipCity ?? '-'} </cr-text>
                  `
                : html`
                    <cr-text> ${this._order.value?.shippingDetails.shippingRecipientName} </cr-text>
                    <cr-text>
                      ${this._order.value?.shippingDetails?.shippingAddress?.strasse}
                      ${this._order.value?.shippingDetails?.shippingAddress?.hausnummer}
                    </cr-text>
                  `}
            </cr-flex>

            <cr-text bold>Lieferanweisungen:</cr-text>
            <cr-text>${unsafeHTML(this._order.value?.shippingDetails.shippingInstructions)}</cr-text>
            ${this._order.value?.trackingLink
              ? html`
                  <cr-text bold>Sendungsverfolgung:</cr-text>
                  <cr-text>
                    <a href=${this._order.value.trackingLink} target="_blank" rel="noopener noreferrer">
                      ${this._order.value.trackingLink}
                    </a>
                  </cr-text>
                `
              : nothing}
          </cr-grid>
          <cr-flex space="md"> </cr-flex>
        </cr-card>
      </cr-flex>
    `;
  }

  private _acceptOrder() {
    if (this._order.isLoading) {
      return html` <x-flex center crossaxis-center flex> <cr-loader></cr-loader> </x-flex> `;
    }

    return html`
      ${choose(
        this._acceptOrderStatus,
        [
          ['pending', () => html``],
          [
            'processing',
            () => html`
              <x-flex center crossaxis-center flex space="md">
                <cr-loader> </cr-loader> <cr-text> Erhalt der Bestellung wird verarbeitet </cr-text>
              </x-flex>
            `,
          ],
          [
            'completed',
            () =>
              html`
                <x-flex center crossaxis-center flex space="md" ${css({ textAlign: 'center' })}>
                  <cr-icon ${css({ fontSize: this.spacing.xl, color: this.colors.tone.success })}> check </cr-icon>
                  <cr-text> Annahme der Bestellung wurde erfolgreich bestätigt! </cr-text>
                  <cr-text tone="subtle">
                    Sie können die Seite jetzt verlassen oder zur Bestellübersicht wechseln.
                  </cr-text>
                  <cr-button href="/order/status/${this._orderID.value}"> Zur Bestellübersicht </cr-button>
                </x-flex>
              `,
          ],
        ],
        () =>
          html`
            <x-flex crossaxis-center center flex space="md" ${css({ textAlign: 'center' })}>
              <cr-icon ${css({ fontSize: this.spacing.xl, color: this.colors.tone.error })}> error </cr-icon>
              <cr-text> Es gab ein Problem beim Verarbeiten der Bestellung. Bitte versuchen Sie es erneut! </cr-text>
              <cr-button @click=${() => (this._acceptOrderStatus = 'pending')}> Erneut versuchen </cr-button>
            </x-flex>
          `,
      )}
    `;
  }

  private async _acceptOrderDelivery() {
    if (this._acceptOrderStatus !== 'pending') return;

    this._acceptOrderStatus = 'processing';
    try {
      if (!this._order.value) throw new Error('Bestellung wurde nicht gefunden. Bitte checken Sie Ihren Link!');

      if (!this._order.value.deliveryStatus) this._order.value.deliveryStatus = new LogistikOrderDeliveryState({});
      (this._order.value as LogistikOrder).deliveryStatus!.status = LogistikOrderDeliveryStateEnum.RECEIVED;
      this._order.value.status = LogistikShippingStatus.COMPLETED;
      await Promise.all([this._order.value.save(), firstValueFrom(timer(500))]);
      this._acceptOrderStatus = 'completed';
    } catch (e) {
      void XSnackbar.show((e as Error).message);
      this._acceptOrderStatus = 'error';
    }
  }
}

/**
 * @customElement logistik-page-order-status
 */
@customElement('logistik-page-order-status')
export class LogistikPageOrderStatus extends ChemistryLitElement {
  override styles() {
    return [...super.styles(), { p: { margin: 0 } }];
  }

  override render() {
    return html` <logistik-component-base-page-layout>
      <logistik-component-base-header pageTitle="Ihre Bestellung" backRoute=${LogistikRoutes.landing}>
      </logistik-component-base-header>
      ${watch(orderRouter.outlet)}
    </logistik-component-base-page-layout>`;
  }
}
